import { Box } from '@hover/blueprint';
import React from 'react';
import { useTransition } from 'react-spring';
import type { SpringValues } from 'react-spring';

import { DesignMenu } from 'src/modules/designStudio/components/DesignMenu';
import type { UILayerProps } from 'src/modules/designStudio/components/DesignStudio/UILayer/types';
import { UIKind } from 'src/modules/designStudio/components/DesignStudio/UILayer/types';
import { AnimatedContainer } from 'src/modules/designStudio/components/DesignStudio/styled';
import { NavBar } from 'src/modules/designStudio/components/NavBar';
import {
  InteractiveRenderingToolbar,
  Toolbar,
} from 'src/modules/designStudio/components/Toolbar';
import { easeInOutSine } from 'src/modules/designStudio/helpers/animationConfig';
import { Mode, UILayerProvider } from 'src/modules/designStudio/providers';
import { useMode } from 'src/shared/hooks';
import { useMountToast } from 'src/shared/providers/Toaster';

interface MobileUILayerProps extends UILayerProps {
  closeMenu: () => void;
}

// SpringValue types don't exactly match up with CSSProperties so we need to
// cast the animated styles to this.
// See https://github.com/pmndrs/react-spring/issues/1645 for more info
type StyleType = SpringValues<{
  bottom: number;
  opacity: number;
  height: string;
  width: string;
}>;

const MobileUI = ({
  shouldDisplayToolbar,
  setMenu,
  menu,
  isOnline,
  viewer,
  jobId,
  requestingOrgName,
  closeMenu,
}: MobileUILayerProps) => {
  const transitions = useTransition(!!menu, {
    from: {
      transform: 'translate3d(0, 100%,0)',
      opacity: 0,
      position: 'absolute',
      bottom: 0,
      height: '100%',
      width: '100%',
    },
    enter: { transform: 'translate3d(0,0%,0)', opacity: 1 },
    leave: { transform: 'translate3d(0,100%,0)', opacity: 1 },
    config: {
      friction: 220,
      duration: 450,
      easing: easeInOutSine,
    },
  });

  useMountToast('toolbar');
  const mode = useMode();
  const isInteractiveRendering = mode === Mode.INTERACTIVE_RENDERING;

  return (
    <UILayerProvider kind={UIKind.MOBILE}>
      {shouldDisplayToolbar && (
        <>
          <NavBar isOnline={isOnline} menu={menu} setMenu={setMenu} />
          <Box flexDirection="column">
            {transitions((menuStyle, item) =>
              item ? (
                <AnimatedContainer style={menuStyle as StyleType}>
                  <DesignMenu
                    closeMenu={closeMenu}
                    jobId={jobId}
                    menu={menu}
                    requestingOrgName={requestingOrgName}
                    toolbarRef={null}
                    viewer={viewer}
                  />
                </AnimatedContainer>
              ) : (
                <AnimatedContainer style={menuStyle as StyleType}>
                  {isInteractiveRendering ? (
                    <InteractiveRenderingToolbar
                      menu={menu}
                      setMenu={setMenu}
                    />
                  ) : (
                    <Toolbar menu={menu} setMenu={setMenu} />
                  )}
                </AnimatedContainer>
              ),
            )}
          </Box>
        </>
      )}
    </UILayerProvider>
  );
};

export { MobileUI };
